import {connect} from 'react-redux';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ConversationSearchResultModelWithOptionalConversation} from '../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {isTrackerUnsnoozed} from '../../../helpers/modelHelpers';
import {
  findUnreadStatus,
  isConversationInTeamContext,
  pickUnreadStatus,
} from '../../../helpers/models/conversationHelpers';
import {CommentStatusesEnum} from '../../../models/api/commentModel';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ConversationSearchResultModel} from '../../../models/api/conversationSearchResultModel';
import {ConversationSendersModel} from '../../../models/api/conversationSendersModel';
import {MessageStatusesEnum, MessageTypesEnum} from '../../../models/api/messageModel';
import {AppState} from '../../../store/appState';
import {getCompany} from '../../../store/data/company/companySelectors';
import {makeGetVisualRecipient} from '../../../store/data/contacts/recipientsSelectors';
import {
  makeGetAssignee,
  makeGetCellCommentingTeammate,
  makeGetCellPrecogs,
  makeGetCellReplyingTeammate,
  makeGetCommentDraftSummaryWithAuthor,
  makeGetConversationInbox,
  makeGetConversationTags,
  makeGetDate,
  makeGetDiscussionTrackerTeammates,
  makeGetDisplayableCommentDraftSummary,
  makeGetIsStarred,
  makeGetLastComment,
  makeGetLastCommentWithAuthor,
  makeGetLastDraftSeedAuthors,
  makeGetLastManualMessage,
  makeGetNumMessages,
  makeGetReminderDate,
  makeGetStatus,
  makeGetTeam,
  makeGetTrackerStatus,
  makeGetVisualSearchActivity,
} from '../../../store/data/conversations/conversationCellSelectors';
import {makeGetVisualConversationSenders} from '../../../store/data/conversations/conversationSendersSelectors';
import {
  getConversationLastDraftMessageSummary,
  makeGetDraftComments,
  makeGetDraftMessages,
} from '../../../store/data/conversations/conversationsSelectors';
import {getFindTeammate} from '../../../store/data/teammates/lazyTeammatesSelectors';
import {getMentionableAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {
  getHasAutoShareDrafts,
  getHasP2QuickActions,
  getHasP2QuickSnooze,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {InboxesConversationsParametersWithSearch} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsExactlyOneIdSelected,
  computeIsIdSelected,
  findParametersViewId,
  InboxesSelection,
} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {VisualSearchResultCommentSummary} from './content/cell/cellActivity';
import {ConversationCell} from './conversationCell';

/*
 * Props.
 */

export interface ConversationSearchResultCellContainerProps {
  parameters: InboxesConversationsParametersWithSearch | undefined;
  cellsContext: CellsContext;
  searchResult: ConversationSearchResultModelWithOptionalConversation;
  selection: InboxesSelection<number>;
  conversationHighlight?: ConversationHighlightModel;
}

/*
 * Selectors.
 */

function getSearchResult(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.searchResult;
}

function getConversationBase(
  _state: AppState,
  props: ConversationSearchResultCellContainerProps,
): ConversationBaseModel {
  // Return most up-to-date conversation base.
  return props.searchResult.conversation ?? props.searchResult;
}

function getCellsContext(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.cellsContext;
}

function getDelegatedTeammateId(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.cellsContext.teammateId;
}

function getParameters(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.parameters;
}

function getSelection(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.selection;
}

function getConversationHighlights(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.conversationHighlight;
}

function getSearchActivity(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.conversationHighlight?.searchActivity;
}

function getFullConversation(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.searchResult.conversation;
}

function makeGetShouldUseMessageForDragAndDrop() {
  return createSelector(
    [getSearchResult, getSelection],
    (searchResult, selection) =>
      !computeIsIdSelected(selection, searchResult.id) || computeIsExactlyOneIdSelected(selection),
  );
}

function findUnreadStatusForConversationSearchResult(
  cellsContext: CellsContext,
  conversationSearchResult: ConversationSearchResultModel,
) {
  const {teammateId} = cellsContext;
  const isInTeamContext = isConversationInTeamContext(cellsContext, conversationSearchResult);

  // Tracker unsnoozed.
  const tracker = conversationSearchResult.trackers[teammateId];
  const isTrackerSnoozeUnread = isTrackerUnsnoozed(conversationSearchResult, tracker);

  // Team unsnoozed.
  const isTeamSnoozeUnread =
    conversationSearchResult.isUnsnoozed &&
    !conversationSearchResult.teamUnsnoozedSeenByIds.includes(teammateId);

  // Message unread.
  const isMessageUnread = !conversationSearchResult.readByIds.includes(teammateId);

  return pickUnreadStatus(isInTeamContext, isTrackerSnoozeUnread, isTeamSnoozeUnread, isMessageUnread);
}

function makeGetUnreadStatusForConversationSearchResult() {
  return createSelector([getSearchResult, getCellsContext], (searchResult, cellsContext) => {
    // The following line allows updating unread status after attaching a store conversation.
    // See maybeAttachConversationToSearchResult.
    if (isDefined(searchResult.conversation)) {
      return findUnreadStatus(cellsContext, searchResult.conversation);
    }

    return findUnreadStatusForConversationSearchResult(cellsContext, searchResult);
  });
}

function makeGetSearchResultCommentSummary(): AppParametricSelector<
  ConversationSearchResultCellContainerProps,
  VisualSearchResultCommentSummary | undefined
> {
  return createSelector([getSearchResult, getFindTeammate], (searchResult, findTeammate) => {
    const {commentSummary} = searchResult;
    if (commentSummary) {
      const author = findTeammate(commentSummary.authorId, 'makeGetSearchResultCommentSummary');

      if (author) {
        return {
          text: commentSummary.text,
          author,
        };
      }
    }

    return undefined;
  });
}

const getLastCommentDraftSummary = createSelector([getSearchResult], (searchResult) => {
  if (searchResult.conversation) {
    return searchResult.conversation.lastCommentDraft;
  }

  const {commentDraft} = searchResult;

  if (!commentDraft) {
    return undefined;
  }

  return {
    id: 0, // Using a dummy id. This is fine as id is never read in the codebase.
    type: MessageTypesEnum.EMAIL, // As per DraftSeed's toJSON in the backend.
    authorId: commentDraft.authorId,
    status: CommentStatusesEnum.DRAFT,
    blurb: commentDraft.text,
    updatedAt: undefined,
    subject: undefined,
    hasAttachments: false, // The backend doesn't give us that information. Empty comment with attachments won't be shown.
    isShared: false,
  };
});

function makeGetDraft() {
  const getDraftMessages = makeGetDraftMessages(getFullConversation);

  return createSelector(
    [getSearchResult, getDraftMessages, getHasAutoShareDrafts],
    (searchResult, draftMessages, hasAutoShareDrafts) => {
      if (searchResult.conversation) {
        return getConversationLastDraftMessageSummary(
          searchResult.conversation,
          draftMessages,
          hasAutoShareDrafts,
        );
      }

      const {lastMessageDraft} = searchResult;

      if (!lastMessageDraft) {
        return undefined;
      }

      return {
        id: 0, // Using a dummy id. This is fine as id is never read in the codebase.
        // When retrieving the seeds from the db we don't have this field and it is not used in the child components.
        // TODO(ldemarne): Come up with a better solution.
        type: MessageTypesEnum.EMAIL,
        // Draft compose is the only type of draft returned by the backend.
        status: MessageStatusesEnum.DRAFT_COMPOSE,
        authorId: lastMessageDraft.authorId,
        blurb: lastMessageDraft.blurb,
        isShared: lastMessageDraft.isShared,
        updatedAt: undefined,
        subject: undefined,
        hasAttachments: false,
      };
    },
  );
}

const getConversationDraftSeeds = createSelector(
  [getSearchResult],
  (searchResult) => searchResult.conversation?.draftSeeds ?? searchResult.messageDraftSeeds,
);

function getSenders(
  _state: AppState,
  props: ConversationSearchResultCellContainerProps,
): ConversationSendersModel | undefined {
  return props.searchResult.senders;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationSearchResultCellContainerProps>()(() => {
  const getVisualUnreadStatus = makeGetUnreadStatusForConversationSearchResult();
  const getCellPrecogs = wrapInShallowSelector(
    makeGetCellPrecogs(getConversationBase, getVisualUnreadStatus),
  );
  const getTrackerTeammates = wrapInShallowSelector(
    makeGetDiscussionTrackerTeammates(getConversationBase, getDelegatedTeammateId),
  );
  const getConversationTags = wrapInShallowSelector(makeGetConversationTags(getConversationBase));
  const getCommentDraftSummary = makeGetDisplayableCommentDraftSummary(getLastCommentDraftSummary);

  const getDraftComments = makeGetDraftComments(getFullConversation);
  const getLastComment = makeGetLastComment(getFullConversation, getDraftComments);

  const getVisualConversationSenders = makeGetVisualConversationSenders(
    getConversationBase,
    getUserId,
    getSenders,
    getConversationHighlights,
  );

  return {
    // ConversationBase.
    date: makeGetDate(getConversationBase, getDelegatedTeammateId, getParameters, getCellsContext),
    status: makeGetStatus(getCellsContext, getConversationBase),
    trackerStatus: makeGetTrackerStatus(getDelegatedTeammateId, getConversationBase),
    trackerTeammates: getTrackerTeammates,
    tags: getConversationTags,
    recipient: makeGetVisualRecipient(
      getConversationBase,
      getDelegatedTeammateId,
      'conversationSearchResultCellContainer',
    ),
    assignee: makeGetAssignee(getConversationBase, getParameters),
    team: makeGetTeam(getConversationBase, getCellsContext),
    inbox: makeGetConversationInbox(getConversationBase, getParameters, getCellsContext),
    commentingTeammate: makeGetCellCommentingTeammate(getCellPrecogs),
    replyingTeammate: makeGetCellReplyingTeammate(getCellPrecogs),
    reminderDate: makeGetReminderDate(getConversationBase, getCellsContext),
    mentionableAliases: getMentionableAliases,
    shouldUseMessageForDragAndDrop: makeGetShouldUseMessageForDragAndDrop(),
    isStarred: makeGetIsStarred(getConversationBase, getDelegatedTeammateId),
    userId: getUserId,
    hasP2QuickActions: getHasP2QuickActions,
    hasP2QuickSnooze: getHasP2QuickSnooze,
    numMessages: makeGetNumMessages(getConversationBase),
    conversation: getConversationBase,
    hasAutoShareDrafts: getHasAutoShareDrafts,

    // Search result specific.
    searchResultCommentSummary: makeGetSearchResultCommentSummary(),
    searchActivity: makeGetVisualSearchActivity(getSearchActivity),
    conversationSenders: getVisualConversationSenders,

    // Full conversation enhances those.
    unreadStatus: getVisualUnreadStatus,
    draft: makeGetDraft(),
    commentDraftSummary: makeGetCommentDraftSummaryWithAuthor(getCommentDraftSummary),
    draftSeedAuthors: makeGetLastDraftSeedAuthors(getConversationDraftSeeds),

    // Full conversation, when present.
    lastComment: makeGetLastCommentWithAuthor(getLastComment),
    lastManualMessage: makeGetLastManualMessage(getFullConversation),
    company: getCompany,
    isSearch: () => true,
  };
});

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  _dispatchProps: unknown,
  ownProps: ConversationSearchResultCellContainerProps,
) {
  // Remove parameters from props since it's not stable and not used.
  const {parameters, ...remainingOwnProps} = ownProps;
  const sourceCustomViewId = parameters && findParametersViewId(parameters);

  return {
    sourceCustomViewId,
    ...remainingOwnProps,
    ...stateProps,
  };
}

/*
 * Component.
 */

export const ConversationSearchResultCellContainer = connect(mapStateToProps, {}, mergeProps, {
  forwardRef: true,
})(ConversationCell);
